import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

import Step1CompanyInfo from '../components/onboarding/Step1CompanyInfo';
import Step2ContactInfo from '../components/onboarding/Step2ContactInfo';
import Step3Insurance from '../components/onboarding/Step3Insurance';
import Step4TaxPayment from '../components/onboarding/Step4TaxPayment';
import Step5Agreement from '../components/onboarding/Step5Agreement';
import Step6Review from '../components/onboarding/Step6Review';

const CarrierOnboardingPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [brokerName, setBrokerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [existingAppId, setExistingAppId] = useState<string | null>(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const [onboardingData, setOnboardingData] = useState({
    companyInfo: {
      carrierName: '',
      mcNumber: '',
      dotNumber: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
    },
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
    },
     insurance: {
      coiFile: null,
      coiFileName: '',
      autoLiability: '',
      cargoInsurance: '',
      generalLiability: '',
      expirationDate: '',
      insuranceEndorsementFile: null,
      insuranceEndorsementFileName: '',
    },
    taxPayment: {
      w9File: null,
      w9FileName: '',
      mcAuthorityFile: null,
      mcAuthorityFileName: '',
      dotAuthorityFile: null,
      dotAuthorityFileName: '',
      voidedCheckFile: null,
      voidedCheckFileName: '',
      paymentMethod: 'ach',
      bankName: '',
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
      factoringCompany: '',
      noaFile: null,
      noaFileName: '',
      additionalDocs: [],
    },
    agreement: {
      agreedToTerms: false,
      signature: '',
      signedDate: '',
    },
  });

  const steps = [
    { number: 1, title: 'Company Info' },
    { number: 2, title: 'Contact Info' },
    { number: 3, title: 'Insurance' },
    { number: 4, title: 'Tax & Payment' },
    { number: 5, title: 'Agreement' },
    { number: 6, title: 'Review' },
  ];

 useEffect(() => {
    const initialize = async () => {
      if (!profile?.company_code) {
        setLoading(false);
        return;
      }

      // Fetch broker name
      const { data, error } = await supabase
        .from('profiles')
        .select('company_name')
        .eq('company_code', profile.company_code)
        .eq('role', 'broker')
        .single();

      if (!error && data) {
        setBrokerName(data.company_name || 'Your Broker');
      }

      // Check for existing application
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data: existingApp } = await supabase
          .from('applications')
          .select('id, form_data')
          .eq('user_id', session.user.id)
          .eq('company_code', profile.company_code)
          .single();

        if (existingApp) {
          setExistingAppId(existingApp.id);
          setIsUpdate(true);
          // Pre-fill form with existing data
          if (existingApp.form_data) {
            setOnboardingData(prev => ({
              ...prev,
              companyInfo: existingApp.form_data.companyInfo || prev.companyInfo,
              contactInfo: existingApp.form_data.contactInfo || prev.contactInfo,
              insurance: { ...prev.insurance, ...existingApp.form_data.insurance, coiFile: null, insuranceEndorsementFile: null },
              taxPayment: { ...prev.taxPayment, ...existingApp.form_data.taxPayment, w9File: null, mcAuthorityFile: null, dotAuthorityFile: null, voidedCheckFile: null, noaFile: null },
              agreement: existingApp.form_data.agreement || prev.agreement,
            }));
          }
        }
      }

      if (profile) {
        setOnboardingData(prev => ({
          ...prev,
          contactInfo: {
            ...prev.contactInfo,
            firstName: profile.first_name || prev.contactInfo.firstName || '',
            lastName: profile.last_name || prev.contactInfo.lastName || '',
          }
        }));
      }
      setLoading(false);
    };

    initialize();
  }, [profile]);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepData = (stepName: string, data: any) => {
    setOnboardingData(prev => ({
      ...prev,
      [stepName]: data,
    }));
  };

  const handleSubmit = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('No user ID');

     // Get company code directly from DB to ensure it's available
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_code')
        .eq('id', userId)
        .single();
      const companyCode = profileData?.company_code || profile?.company_code || '';

      // Count how many docs are uploaded
      let docsUploaded = 0;

      const uploadFile = async (file: File | null, fileName: string, docType: string) => {
        if (!file || !fileName) return null;

        const filePath = `${userId}/${docType}_${Date.now()}_${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('carrier-documents')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          return null;
        }

        const { data: docData, error: docError } = await supabase
          .from('documents')
          .insert({
            user_id: userId,
            type: docType,
            name: fileName,
            file_url: filePath,
            file_type: file.type,
            file_size: file.size,
            status: 'pending',
            uploaded_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (docError) {
          console.error('Document save error:', docError);
          return null;
        }

        docsUploaded++;
        return docData;
      };

      if (onboardingData.insurance.coiFile) {
        await uploadFile(
          onboardingData.insurance.coiFile as File,
          onboardingData.insurance.coiFileName,
          'coi'
        );
      }

      if (onboardingData.taxPayment.w9File) {
        await uploadFile(
          onboardingData.taxPayment.w9File as File,
          onboardingData.taxPayment.w9FileName,
          'w9'
        );
      }

      if (onboardingData.taxPayment.noaFile) {
        await uploadFile(
          onboardingData.taxPayment.noaFile as File,
          onboardingData.taxPayment.noaFileName,
          'noa'
        );
      }

      // Upload Insurance Endorsement
      if (onboardingData.insurance.insuranceEndorsementFile) {
        await uploadFile(
          onboardingData.insurance.insuranceEndorsementFile as File,
          onboardingData.insurance.insuranceEndorsementFileName,
          'insurance_endorsement'
        );
      }

      // Upload MC Authority
      if (onboardingData.taxPayment.mcAuthorityFile) {
        await uploadFile(
          onboardingData.taxPayment.mcAuthorityFile as File,
          onboardingData.taxPayment.mcAuthorityFileName,
          'mc_authority'
        );
      }

      // Upload DOT Authority
      if (onboardingData.taxPayment.dotAuthorityFile) {
        await uploadFile(
          onboardingData.taxPayment.dotAuthorityFile as File,
          onboardingData.taxPayment.dotAuthorityFileName,
          'dot_authority'
        );
      }

      // Upload Voided Check
      if (onboardingData.taxPayment.voidedCheckFile) {
        await uploadFile(
          onboardingData.taxPayment.voidedCheckFile as File,
          onboardingData.taxPayment.voidedCheckFileName,
          'voided_check'
        );
      }

    // Check broker's carrier limit before submitting
      if (companyCode) {
        const { data: brokerProfile } = await supabase
          .from('profiles')
          .select('carrier_limit, subscription_status, trial_ends_at')
          .eq('company_code', companyCode)
          .eq('role', 'broker')
          .single();

        const carrierLimit = brokerProfile?.carrier_limit || 20;

        const { count: currentCarriers } = await supabase
          .from('applications')
          .select('id', { count: 'exact', head: true })
          .eq('company_code', companyCode);

        if (currentCarriers !== null && currentCarriers >= carrierLimit) {
          alert('This broker has reached their carrier limit. Please contact the broker to upgrade their plan.');
          return;
        }

        if (brokerProfile?.subscription_status === 'trial' && brokerProfile?.trial_ends_at) {
          if (new Date(brokerProfile.trial_ends_at) < new Date()) {
            alert('This broker\'s trial has expired. Please contact the broker.');
            return;
          }
        }
    }

   if (existingAppId) {
        // Update existing application
        const { error } = await supabase
          .from('applications')
          .update({
            form_data: onboardingData,
            status: 'submitted',
            current_step: 6,
            submitted_at: new Date().toISOString(),
          })
          .eq('id', existingAppId);

        if (error) throw error;
      } else {
        // New application
        const { error } = await supabase
          .from('applications')
          .insert({
            user_id: userId,
            company_code: companyCode,
            form_data: onboardingData,
            status: 'submitted',
            current_step: 6,
            submitted_at: new Date().toISOString(),
          });

        if (error) throw error;
      }

      await supabase
        .from('profiles')
        .update({
          mc_number: onboardingData.companyInfo.mcNumber,
          dot_number: onboardingData.companyInfo.dotNumber,
          company_name: onboardingData.companyInfo.carrierName,
          phone_number: onboardingData.contactInfo.phone,
        })
        .eq('id', userId);

      // Notify broker — new carrier joined
      const carrierName = `${onboardingData.contactInfo.firstName} ${onboardingData.contactInfo.lastName}`.trim();
await supabase.from('notifications').insert({
        company_code: companyCode,
        title: isUpdate ? 'Carrier Updated Application' : 'New Carrier Joined',
        message: isUpdate
          ? `${carrierName || 'A carrier'} updated their application`
          : `${carrierName || 'A new carrier'} has applied to join your company`,
        type: 'carrier_joined',
      });

      // Notify broker — documents submitted (if any were uploaded)
      if (docsUploaded > 0) {
        await supabase.from('notifications').insert({
          company_code: companyCode,
          title: 'Documents Submitted',
          message: `${carrierName || 'A carrier'} submitted ${docsUploaded} document${docsUploaded > 1 ? 's' : ''} for review`,
          type: 'docs_submitted',
        });
      }

      localStorage.setItem('loaddrive_onboarding_complete', 'true');
      navigate('/carrier/dashboard');
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
       <Step1CompanyInfo
  data={onboardingData.companyInfo}
  onNext={handleNext}
  onSave={(data: any) => handleStepData('companyInfo', data)}
/>
        );
      case 2:
        return (
          <Step2ContactInfo
            data={onboardingData.contactInfo}
            onNext={handleNext}
            onBack={handleBack}
            onSave={(data) => handleStepData('contactInfo', data)}
          />
        );
      case 3:
        return (
          <Step3Insurance
            data={onboardingData.insurance}
            onNext={handleNext}
            onBack={handleBack}
            onSave={(data) => handleStepData('insurance', data)}
          />
        );
      case 4:
        return (
          <Step4TaxPayment
            data={onboardingData.taxPayment}
            onNext={handleNext}
            onBack={handleBack}
            onSave={(data) => handleStepData('taxPayment', data)}
          />
        );
      case 5:
        return (
          <Step5Agreement
            data={onboardingData.agreement}
            brokerName={brokerName}
            onNext={handleNext}
            onBack={handleBack}
            onSave={(data) => handleStepData('agreement', data)}
          />
        );
      case 6:
        return (
          <Step6Review
            data={onboardingData}
            brokerName={brokerName}
            onBack={handleBack}
            onSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src="/images/loaddrive-logo.png" alt="LoadDrive" className="h-8" />
              <span className="text-sm text-gray-500">Carrier Portal</span>
            </div>
            <button
              onClick={() => navigate('/carrier/dashboard')}
              className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isUpdate ? 'Update Application' : 'Carrier Onboarding'}
            </h1>
            <p className="text-gray-600">
              Complete your carrier profile for {brokerName || 'your broker'}
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                        step.number === currentStep
                          ? 'bg-blue-600 text-white'
                          : step.number < currentStep
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {step.number < currentStep ? '\u2713' : step.number}
                    </div>
                    <div className="text-xs text-center text-gray-600 mt-2 max-w-[80px]">
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        step.number < currentStep ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarrierOnboardingPage;
